
import React, { useState, useEffect } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { useIsMobile } from '@/hooks/use-mobile';

interface DebugPanelProps {
  showOnlyInDev?: boolean;
}

export const DebugPanel: React.FC<DebugPanelProps> = ({ showOnlyInDev = true }) => {
  const { user, userRole, session } = useAuth();
  const [expanded, setExpanded] = useState(false);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const isMobile = useIsMobile();
  
  // Environment info
  const [browserInfo, setBrowserInfo] = useState<Record<string, string | number>>({});
  const [performanceMetrics, setPerformanceMetrics] = useState<Record<string, any>>({});
  
  // Only show in development mode if requested
  if (showOnlyInDev && process.env.NODE_ENV !== 'development') {
    return null;
  }
  
  // Get browser and environment info
  useEffect(() => {
    const browser = {
      userAgent: navigator.userAgent,
      language: navigator.language,
      platform: navigator.platform,
      vendor: navigator.vendor,
      screenWidth: window.innerWidth,
      screenHeight: window.innerHeight,
      pixelRatio: window.devicePixelRatio
    };
    setBrowserInfo(browser);
    
    if (window.performance) {
      setTimeout(() => {
        const perf = {
          memory: (window.performance as any).memory ? {
            jsHeapSizeLimit: Math.round(((window.performance as any).memory.jsHeapSizeLimit / 1048576) * 100) / 100,
            totalJSHeapSize: Math.round(((window.performance as any).memory.totalJSHeapSize / 1048576) * 100) / 100,
            usedJSHeapSize: Math.round(((window.performance as any).memory.usedJSHeapSize / 1048576) * 100) / 100,
          } : 'Not available',
          navigation: window.performance.getEntriesByType('navigation')[0],
          timings: {
            dns: (window.performance.getEntriesByType('navigation')[0] as any)?.domainLookupEnd - (window.performance.getEntriesByType('navigation')[0] as any)?.domainLookupStart,
            tcp: (window.performance.getEntriesByType('navigation')[0] as any)?.connectEnd - (window.performance.getEntriesByType('navigation')[0] as any)?.connectStart,
            request: (window.performance.getEntriesByType('navigation')[0] as any)?.responseStart - (window.performance.getEntriesByType('navigation')[0] as any)?.requestStart,
            response: (window.performance.getEntriesByType('navigation')[0] as any)?.responseEnd - (window.performance.getEntriesByType('navigation')[0] as any)?.responseStart,
            dom: (window.performance.getEntriesByType('navigation')[0] as any)?.domComplete - (window.performance.getEntriesByType('navigation')[0] as any)?.domInteractive,
          }
        };
        setPerformanceMetrics(perf);
      }, 1000);
    }
  }, []);
  
  // Get a shortened version of tokens for display
  const shortenToken = (token?: string) => {
    if (!token) return 'none';
    return token.substring(0, 10) + '...' + token.substring(token.length - 10);
  };
  
  // Handle dragging for repositioning
  const startDragging = (e: React.MouseEvent) => {
    setIsDragging(true);
    setPosition({
      x: e.clientX,
      y: e.clientY
    });
  };
  
  const handleDrag = (e: React.MouseEvent) => {
    if (!isDragging) return;
    
    const debugPanel = e.currentTarget.parentElement;
    if (debugPanel) {
      const rect = debugPanel.getBoundingClientRect();
      const newX = Math.max(10, Math.min(window.innerWidth - rect.width - 10, rect.left + e.clientX - position.x));
      const newY = Math.max(10, Math.min(window.innerHeight - rect.height - 10, rect.top + e.clientY - position.y));
      
      debugPanel.style.left = `${newX}px`;
      debugPanel.style.top = `${newY}px`;
      debugPanel.style.right = 'auto';
      debugPanel.style.bottom = 'auto';
      
      setPosition({
        x: e.clientX,
        y: e.clientY
      });
    }
  };
  
  const stopDragging = () => {
    setIsDragging(false);
  };
  
  // Special actions for debugging
  const clearLocalStorage = () => {
    localStorage.clear();
    sessionStorage.clear();
    alert('Local storage and session storage cleared!');
  };
  
  const toggleUserRole = async () => {
    // In production this would do nothing
    if (process.env.NODE_ENV !== 'development') return;
    
    alert('Role switching would be implemented here in development mode');
    // This is a placeholder for role-switching functionality
    // In a real implementation, you would update the role in the AuthContext
  };
  
  return (
    <div 
      className={`fixed bottom-4 right-4 z-[9999] transition-all duration-300 ${expanded ? 'w-96' : 'w-10'}`}
      style={{ right: expanded ? '1rem' : '1rem', bottom: expanded ? '1rem' : '1rem' }}
      onMouseDown={startDragging}
      onMouseMove={handleDrag}
      onMouseUp={stopDragging}
      onMouseLeave={stopDragging}
    >
      <div 
        className="absolute top-2 right-2 bg-[#2C6E49] text-white w-6 h-6 rounded-full flex items-center justify-center cursor-pointer z-10"
        onClick={(e) => {
          e.stopPropagation();
          setExpanded(!expanded);
        }}
      >
        {expanded ? '×' : 'D'}
      </div>
      
      <div className={`bg-black/80 text-white p-3 rounded-lg backdrop-blur-lg border border-white/10 overflow-hidden transition-all duration-300 ${expanded ? 'max-h-[90vh] opacity-100' : 'max-h-0 opacity-0'}`}>
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-sm font-bold text-[#2C6E49]">CHRONA Debug Panel</h3>
          <div className="text-xs text-white/50">{process.env.NODE_ENV}</div>
        </div>
        
        <div className="text-xs space-y-2 overflow-y-auto max-h-[70vh] pr-2">
          <div className="mb-4 p-2 bg-white/5 rounded">
            <div className="font-medium mb-1 text-[#4C956C]">User Information</div>
            <div><span className="text-zinc-400">User ID:</span> {user?.id || 'Not logged in'}</div>
            <div><span className="text-zinc-400">Email:</span> {user?.email || 'N/A'}</div>
            <div><span className="text-zinc-400">Role:</span> <span className="bg-[#2C6E49]/50 px-2 py-0.5 rounded">{userRole || 'none'}</span></div>
          </div>
          
          <div className="mb-4 p-2 bg-white/5 rounded">
            <div className="font-medium mb-1 text-[#4C956C]">Device Information</div>
            <div><span className="text-zinc-400">Viewport:</span> {browserInfo.screenWidth}x{browserInfo.screenHeight} (x{browserInfo.pixelRatio})</div>
            <div><span className="text-zinc-400">Platform:</span> {browserInfo.platform}</div>
            <div><span className="text-zinc-400">Mobile:</span> {isMobile ? 'Yes' : 'No'}</div>
          </div>
          
          <details className="mb-4 p-2 bg-white/5 rounded">
            <summary className="cursor-pointer font-medium text-[#4C956C]">Session Details</summary>
            <div className="mt-2 p-2 bg-black/60 rounded max-h-40 overflow-auto">
              <div><span className="text-zinc-400">Access Token:</span> {shortenToken(session?.access_token)}</div>
              <div><span className="text-zinc-400">Refresh Token:</span> {shortenToken(session?.refresh_token)}</div>
              <div><span className="text-zinc-400">Expires At:</span> {session?.expires_at ? new Date(session.expires_at * 1000).toLocaleTimeString() : 'N/A'}</div>
            </div>
          </details>
          
          <details className="mb-4 p-2 bg-white/5 rounded">
            <summary className="cursor-pointer font-medium text-[#4C956C]">Performance Metrics</summary>
            <div className="mt-2 p-2 bg-black/60 rounded max-h-40 overflow-auto">
              {performanceMetrics.memory && typeof performanceMetrics.memory === 'object' && (
                <div className="mb-2">
                  <div className="text-zinc-400 mb-1">Memory Usage:</div>
                  <div>Used: {performanceMetrics.memory.usedJSHeapSize} MB</div>
                  <div>Total: {performanceMetrics.memory.totalJSHeapSize} MB</div>
                  <div>Limit: {performanceMetrics.memory.jsHeapSizeLimit} MB</div>
                </div>
              )}
              
              {performanceMetrics.timings && (
                <div>
                  <div className="text-zinc-400 mb-1">Timing:</div>
                  <div>DNS: {performanceMetrics.timings.dns?.toFixed(2)} ms</div>
                  <div>TCP: {performanceMetrics.timings.tcp?.toFixed(2)} ms</div>
                  <div>Request: {performanceMetrics.timings.request?.toFixed(2)} ms</div>
                  <div>Response: {performanceMetrics.timings.response?.toFixed(2)} ms</div>
                  <div>DOM: {performanceMetrics.timings.dom?.toFixed(2)} ms</div>
                </div>
              )}
            </div>
          </details>
          
          <details className="mb-4 p-2 bg-white/5 rounded">
            <summary className="cursor-pointer font-medium text-[#4C956C]">Local Storage</summary>
            <div className="mt-2 p-2 bg-black/60 rounded max-h-40 overflow-auto">
              {Object.keys(localStorage).map(key => (
                <div key={key} className="mb-1">
                  <span className="text-zinc-400">{key}:</span> {localStorage.getItem(key)?.substring(0, 30)}{localStorage.getItem(key)?.length > 30 ? '...' : ''}
                </div>
              ))}
              {Object.keys(localStorage).length === 0 && <div>No items in localStorage</div>}
            </div>
          </details>
          
          {/* Debug Actions */}
          <div className="mt-4 pt-2 border-t border-white/10">
            <div className="font-medium mb-2 text-[#4C956C]">Debug Actions</div>
            <div className="flex flex-wrap gap-2">
              <button 
                onClick={clearLocalStorage}
                className="px-2 py-1 bg-red-900/50 hover:bg-red-800/50 rounded text-xs"
              >
                Clear Storage
              </button>
              
              <button 
                onClick={toggleUserRole}
                className="px-2 py-1 bg-blue-900/50 hover:bg-blue-800/50 rounded text-xs"
                disabled={process.env.NODE_ENV !== 'development'}
              >
                Toggle Role
              </button>
              
              <button 
                onClick={() => window.location.reload()}
                className="px-2 py-1 bg-[#2C6E49]/50 hover:bg-[#2C6E49]/70 rounded text-xs"
              >
                Reload App
              </button>
            </div>
          </div>
          
          <div className="mt-4 text-center text-[10px] text-zinc-500">
            CHRONA Debug Panel v1.0 - Development Use Only
          </div>
        </div>
      </div>
    </div>
  );
};
